import React, { useEffect, useState } from 'react';
import { Alert, Button, Box, CircularProgress, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  getFacilityCorporateStatus,
  updateToLatestCorporateVersion,
} from '../../../firebase/functions';
import { formatSeason } from '../../../utils/corporateMenu';

function CorporateMenuStatus({ facilityId, season, year, onUpdate }) {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, [facilityId, season, year]);

  const fetchStatus = async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await getFacilityCorporateStatus({
        facilityId,
        season,
        year,
      });
      setStatus(result.data);
    } catch (err) {
      setError(err.message || 'Failed to load corporate menu status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateToLatest = async () => {
    setUpdating(true);
    setError('');
    try {
      const result = await updateToLatestCorporateVersion({
        facilityId,
        season,
        year,
      });

      if (result.data.success) {
        await fetchStatus();
        onUpdate && onUpdate();
      }
    } catch (err) {
      setError(err.message || 'Failed to update to latest version');
    } finally {
      setUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CircularProgress size={20} />
        <span>Loading corporate menu status...</span>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!status || !status.hasAssignment) {
    return null;
  }

  const { assignment, latestVersion, updateAvailable } = status;
  // Handle either ISO string or Firestore Timestamp-like objects
  let startDate;
  if (assignment.startDateApplied?.toDate) {
    startDate = dayjs(assignment.startDateApplied.toDate());
  } else {
    startDate = dayjs(assignment.startDateApplied);
  }

  return (
    <Alert
      severity={updateAvailable ? 'warning' : 'info'}
      sx={{ mb: 2 }}
      action={
        updateAvailable ? (
          <Button
            color="inherit"
            size="small"
            onClick={handleUpdateToLatest}
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Update to Latest'}
          </Button>
        ) : null
      }
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        <strong>Corporate Menu Active:</strong>
        <Chip
          label={`${formatSeason(assignment.season)} ${assignment.year}`}
          size="small"
          color="primary"
        />
        <span>•</span>
        <span>Version: {assignment.versionApplied}</span>
        <span>•</span>
        <span>Started: {startDate.format('MMM D, YYYY')}</span>
        {updateAvailable && (
          <>
            <span>•</span>
            <Chip
              label="Update Available"
              size="small"
              color="warning"
              variant="outlined"
            />
          </>
        )}
      </Box>
    </Alert>
  );
}

CorporateMenuStatus.propTypes = {
  facilityId: PropTypes.string.isRequired,
  season: PropTypes.string,
  year: PropTypes.number,
  onUpdate: PropTypes.func,
};

export default CorporateMenuStatus;
